import React, { ReactNode } from 'react';
import AKAvatar from '@atlaskit/avatar';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Flex } from '@atlaskit/primitives';
import { colors, gridSize } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Lozenge from '@atlaskit/lozenge';

export interface HasPrimary {
  primary?: boolean;
}

export interface Props extends AvatarProps {
  displayName?: string;
  children?: ReactNode;
  onClick?: (_: React.MouseEvent<HTMLElement>) => void;
  email: string;
  header?: ReactNode;
  icon?: ReactNode;
  isolatedCloudLabel?: string;
  id?: string;
}

export interface AccountContainerProps extends HasPrimary {
  onClick?: (_: React.MouseEvent<HTMLElement>) => void;
  id?: string;
  noBorder?: boolean;
}

export interface AvatarProps extends HasPrimary {
  avatarUrl?: string;
}

export const AccountContainer = styled.button<AccountContainerProps>`
  /* reset button styles */
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  width: 100%;
  text-align: left;

  display: flex;
  align-items: center;
  flex-direction: column;
  padding-top: ${props => (props.primary ? '0' : '16px')};
  padding-bottom: ${props => (props.primary ? '24px' : '16px')};

  &:last-child {
    border-bottom: 1px solid ${colors.N30A};
    ${props =>
      props.noBorder &&
      css`
        border-bottom: none;
        margin-bottom: ${-gridSize() * 3}px;
      `}
  }

  ${props =>
    props.primary &&
    `  &:only-child {
        margin-bottom: 0;
        border-bottom: 0;
        padding: 8px 0;
    }
      `}

  ${props =>
    props.onClick
      ? `&:hover, &:focus {
      border-radius: 3px;
      transition: background 0s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38);
      cursor: pointer;
      background-color: ${colors.N30A};
    }`
      : ''}
`;

const primaryAvatarSize = '72px';
const defaultAvatarSize = '36px';

export const AvatarContainer = styled.div<HasPrimary>`
  margin: 0 0 0 ${props => (props.primary ? '0' : '8px')};
  width: ${props => (props.primary ? primaryAvatarSize : defaultAvatarSize)};
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.primary && `padding-left: 8px;`}

  ${props =>
    props.primary &&
    `
    div, span {
      padding: 0;
      width: ${primaryAvatarSize} !important;
      height: ${primaryAvatarSize} !important;
    }
  `}
`;

const Avatar = (props: AvatarProps) => (
  <AvatarContainer primary={props.primary}>
    <AKAvatar
      appearance="circle"
      src={props.avatarUrl}
      size={props.primary ? 'xlarge' : 'medium'}
    />
  </AvatarContainer>
);

export const RowContainer = styled.section`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

export const RowButtonContainer = styled(RowContainer)`
  padding: 8px 0;
`;

export const TextContainer = styled.section`
  flex-grow: 1;

  min-width: 0;
  // IE11 expands width to the parent container until the overflow kicks in
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    width: 0;
  }

  display: inline-block;
  margin: 0 16px;
`;

const Title = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Header = styled.span<HasPrimary>`
  display: flex;
  gap: ${token('space.050')};
  align-items: center;
  font: ${props => (props.primary ? token('font.heading.medium') : token('font.heading.xsmall'))};
  font-size: ${props => (props.primary ? '20px' : '14px')};
  line-height: ${props => (props.primary ? '24px' : '20px')};
  color: ${colors.N800};
  overflow-wrap: break-word;
`;

export const Paragraph = styled.p<HasPrimary>`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.N700};
  margin-top: ${props => (props.primary ? '4px' : '0')};
  overflow-wrap: break-word;
`;

export const IconContainer = styled.section`
  margin-right: 8px;
`;

/**
 * Representation of an account in a row format, used for rendering lists of accounts, e.g. for switching an account
 * or selecting one to log in
 */
const Account = ({
  avatarUrl,
  children,
  displayName,
  email,
  header,
  icon,
  isolatedCloudLabel,
  id,
  onClick,
  primary,
}: Props) => {
  const title = displayName ? displayName : email;
  const paragraph = displayName ? email : undefined;

  return (
    <AccountContainer
      id={id}
      className="account-entry" // account-entry used by pollinators
      data-testid="account-entry"
      as={primary ? 'div' : 'button'}
      primary={primary}
      onClick={onClick}>
      {header}
      <RowContainer>
        <Avatar avatarUrl={avatarUrl} primary={primary} />
        <TextContainer>
          <Header primary={primary}>
            <Title data-testid="email">{title}</Title>
            {isolatedCloudLabel && (
              <Flex>
                <Lozenge maxWidth="130px">{isolatedCloudLabel}</Lozenge>
              </Flex>
            )}
          </Header>
          {paragraph && <Paragraph primary={primary}>{paragraph}</Paragraph>}
        </TextContainer>
        <IconContainer>{icon}</IconContainer>
      </RowContainer>
      {children}
    </AccountContainer>
  );
};

export default Account;
